import { put, call, race, take, delay, cancel } from 'redux-saga/effects'

import * as types from '../actions/actionTypes'
import { setSnackbarErrorAction } from '../actions/snackbarActions'
import {
  refreshTokenAction,
  logoutAction,
  vendorLoginAction,
} from '../actions/userActions'
import { requestRefreshToken, requestVendorLogin, requestLogin } from '../api'
import { logout } from '../api/UserAPI'
import { getErrorMessage } from '../domain/Errno'
import * as auth from '../libs/auth'
import { stripQueryString } from '../libs/historyHelper'
import { to886 } from '../libs/utils'
import { FETCH_LANDING_STATES } from '../reducers/landingReducer'
import { VENDOR_LOGIN_STATES } from '../reducers/uiReducer'
import { VENDOR_LOGIN_PARAMS } from '../reducers/userReducer'

export function* authFlowSaga() {
  while (true) {
    const idToken = auth.readIdToken()
    if (!idToken.idToken) {
      yield take(types.LOGIN_SUCCESS)
    }
    let isLoggedIn = true
    while (isLoggedIn) {
      const expireInfo = auth.readExpireInfo()
      const delayTime = ((expireInfo && expireInfo.expiresIn) || 3600) * 1000
      const { timeout, logout } = yield race({
        timeout: delay(delayTime),
        refresh: take(types.REFRESH_TOKEN_SUCCESS),
        logout: race({
          success: take(types.LOGOUT_SUCCESS),
          error: take(types.LOGOUT_ERROR),
        }),
      })
      if (timeout) {
        const refreshTokenPayload = auth.readRefreshTokenPayload()
        if (refreshTokenPayload) {
          yield put(refreshTokenAction(refreshTokenPayload, true))
          const { error } = yield race({
            success: take(types.REFRESH_TOKEN_SUCCESS),
            error: take(types.REFRESH_TOKEN_ERROR),
          })
          if (error) {
            isLoggedIn = false
            yield put(logoutAction())
          }
        } else {
          isLoggedIn = false
          yield put(logoutAction())
        }
      } else if (logout) {
        isLoggedIn = false
      }
    }
  }
}

export function* attemptRefreshToken() {
  const idToken = auth.readIdToken()

  if (!idToken.idToken) {
    return
  }

  if (!idToken.isExpired) {
    return
  }

  if (idToken.isRefreshing) {
    const { error } = yield race({
      success: take(types.REFRESH_TOKEN_SUCCESS),
      error: take(types.REFRESH_TOKEN_ERROR),
    })
    if (error) {
      yield cancel()
    }
    return
  }

  const refreshTokenPayload = auth.readRefreshTokenPayload()
  if (!refreshTokenPayload) {
    yield cancel()
    yield put(logoutAction())
    return
  }

  auth.setIsRefreshing(true)
  yield put(refreshTokenAction(refreshTokenPayload, true))
  const { error } = yield race({
    success: take(types.REFRESH_TOKEN_SUCCESS),
    error: take(types.REFRESH_TOKEN_ERROR),
  })
  auth.setIsRefreshing(false)
  if (error) {
    yield cancel()
    yield put(logoutAction())
    return
  }

  yield delay(100)
}

export function* loginSaga({ payload }) {
  try {
    yield put({ type: types.FETCHING })
    // TODO [future] convert 09xx to +8869xx; other country?
    const username = to886(payload.username)

    const res = yield call(requestLogin, {
      username,
      password: payload.password,
    })
    const user = res.data

    if (payload.isBuyer) {
      yield put({
        type: types.SET_LANDING_FETCH_STATE,
        payload: FETCH_LANDING_STATES.IS_BUYER,
      })
    }

    yield put({
      type: types.LOGIN_SUCCESS,
      payload: {
        ...user,
        username,
      },
    })
  } catch (err) {
    const { onError } = payload
    if (typeof onError === 'function') {
      try {
        onError(err)
      } catch (ignored) {
        // continue
      }
    } else {
      console.error('loginSaga', err)
      const message = getErrorMessage(err)
      yield put({
        type: types.LOGIN_ERROR,
        payload: { message },
      })
      yield put(setSnackbarErrorAction(message))
    }
  } finally {
    yield put({ type: types.FETCH_COMPLETE })
  }
}

export function* refreshTokenSaga({ payload }) {
  if (!payload.skipLoading) {
    yield put({ type: types.FETCHING })
  }
  try {
    const res = yield call(requestRefreshToken, {
      username: payload.username,
      refresh_token: payload.refresh_token,
    })
    yield put({
      type: types.REFRESH_TOKEN_SUCCESS,
      payload: res.data,
    })
  } catch (err) {
    console.error('refreshTokenSaga', err)
    yield put({
      type: types.REFRESH_TOKEN_ERROR,
    })
  }
  if (!payload.skipLoading) {
    yield put({ type: types.FETCH_COMPLETE })
  }
}

export function* vendorLoginSaga({ payload }) {
  try {
    yield put({ type: types.FETCHING })
    const res = yield call(requestVendorLogin, payload.params)
    const user = res.data
    yield put({
      type: types.LOGIN_SUCCESS,
      payload: {
        ...user,
        username: payload.username || user.email,
      },
    })
    yield put({
      type: types.SET_VENDOR_LOGIN_STATE,
      payload: VENDOR_LOGIN_STATES.SUCCEEDED,
    })
  } catch (err) {
    console.error('vendorLoginSaga', err)
    const message = getErrorMessage(err)
    yield put({
      type: types.LOGIN_ERROR,
      payload: { message },
    })
    yield put(setSnackbarErrorAction(message))
  } finally {
    yield put({ type: types.FETCH_COMPLETE })
    yield call(stripQueryString, VENDOR_LOGIN_PARAMS)
  }
}

export function* logoutSaga({ payload }) {
  if (!payload.idToken) {
    yield put({ type: types.LOGOUT_SUCCESS })
    return
  }
  try {
    yield put({ type: types.FETCHING })
    yield call(logout)
    yield put({
      type: types.LOGOUT_SUCCESS,
    })
    yield put(setSnackbarErrorAction('已成功登出'))
  } catch (err) {
    console.error('logoutSaga', err)
    yield put({
      type: types.LOGOUT_ERROR,
    })
  } finally {
    yield put({ type: types.FETCH_COMPLETE })
  }
}

export function* cleanVendorLoginSaga({ payload }) {
  // * skip logout API even if already authenticated
  yield put({ type: types.LOGOUT_SUCCESS })
  /*
  if (payload.authenticated) {
    yield put(logoutAction())
    yield race({
      success: take(types.LOGOUT_SUCCESS),
      error: take(types.LOGOUT_ERROR),
    })
  }
  */
  yield put(vendorLoginAction(payload))
}
